import { ReactNode } from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { DEALERSHIP_NAME } from '@/data/mockData';
import { Logo } from '@/components/Logo';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { User as UserIcon } from 'lucide-react';
import { ModeBadge } from '@/components/ModeBadge';

export function DashboardLayout({ children }: { children: ReactNode }) {
  const { userName, role, mode, dealershipName, isSolo } = useAuth();
  const isDemo = mode === 'demo';
  const isRealSolo = mode === 'real' && isSolo;
  const displayDealership = isDemo ? DEALERSHIP_NAME : (dealershipName || DEALERSHIP_NAME);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between border-b border-border px-4 bg-background/80 backdrop-blur-sm sticky top-0 z-40">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <Logo variant="app-auto" className="h-7 lg:h-8 object-contain" />
              <span className="text-sm font-semibold text-foreground hidden sm:inline">{displayDealership}</span>
              <ModeBadge />
              {isRealSolo && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      role="status"
                      aria-label="Solo workspace"
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-accent/40 text-foreground border border-border text-[10px] font-bold uppercase tracking-wider cursor-help"
                    >
                      <UserIcon className="h-3 w-3" />
                      Solo
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-[260px] text-xs">
                    Your private workspace. Only you can see these deals. Rename it anytime in Settings.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm">{userName}</span>
              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium capitalize">{role}</span>
            </div>
          </header>
          <main className="flex-1 p-4 lg:p-6 overflow-x-hidden overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
